// Ayuda EXCLUSIVA de Telemedicina. Centro Medico → Resumen → Ayuda Telemedicina.
// Patrón ayuda-de-modulo: checklist en vivo + flujo de la teleconsulta +
// ¿cómo hago...? con ejemplos + glosario. El video corre en NUESTRO servidor
// (LiveKit self-hosted) — cumple la Norma Técnica de Telesalud (A.M. 00044-2025)
// y la LOPDP (los datos de salud no salen a terceros tipo Zoom/Meet).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronRight, Link2, Loader2, Video, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { medRpc } from './shared';

interface AyudaData {
  checklist: Record<string, boolean>;
  estado: { tele_hoy: number; en_espera: number; efectivas_mes: number };
}

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

const CHECKS: { key: string; label: string; ruta: string; titulo: string; ayuda: string; advisory?: boolean }[] = [
  { key: 'video', label: 'Servidor de video propio configurado', ruta: '/medical/teleconsulta', titulo: 'Teleconsulta', ayuda: 'LiveKit self-hosted (app.livekit_url): la videollamada corre en nuestro servidor, no en Zoom/Meet.' },
  { key: 'medicos_tarifa', label: 'Médicos con precio de consulta', ruta: '/medical/medicos', titulo: 'Médicos', ayuda: 'La tarifa del PREPAGO sale del precio de consulta del médico; sin precio, la cita queda exonerada.', advisory: true },
  { key: 'deuna', label: 'De Una (QR) configurado', ruta: '/pos/deuna', titulo: 'De Una (QR)', ayuda: 'Para que el paciente pague con QR del Pichincha desde el portal (queda transferencia y marca manual si no).', advisory: true },
  { key: 'canales', label: 'Canales de notificación activos', ruta: '/medical/canales', titulo: 'Canales de notificación', ayuda: 'Recordatorios de la teleconsulta por Email/WhatsApp con el enlace del portal.', advisory: true },
  { key: 'portal', label: 'Pacientes con acceso al portal', ruta: '/medical/pacientes', titulo: 'Pacientes', ayuda: 'El paciente entra a la sala SOLO autenticado en su portal — así se verifica su identidad (Norma 7.2).' },
];

const FLUJO = [
  { n: 1, titulo: 'Agendar la cita telemática', detalle: 'En la Agenda, cita con tipo de atención "telemática". Nace con la tarifa del médico y pago PENDIENTE.', ruta: '/medical/agenda', tituloTab: 'Agenda' },
  { n: 2, titulo: 'El paciente paga desde su portal', detalle: 'Botón "Pagar": De Una QR (confirma solo) o transferencia con comprobante (el staff la valida). Al pagar se emite la FACTURA SRI automáticamente.', ruta: '/medical/teleconsulta', tituloTab: 'Teleconsulta' },
  { n: 3, titulo: 'Consentimiento telemático', detalle: 'El paciente acepta el consentimiento de telemedicina (checklist + autorización de grabación NO, por defecto no se graba). Sin esto no entra a la sala.', ruta: '/medical/teleconsulta', tituloTab: 'Teleconsulta' },
  { n: 4, titulo: 'Prueba de cámara y micrófono', detalle: 'Antes de entrar, el paciente se ve en pantalla y prueba el micrófono; puede elegir "solo audio" si su red es lenta.', ruta: '/medical/teleconsulta', tituloTab: 'Teleconsulta' },
  { n: 5, titulo: 'Sala de espera virtual', detalle: 'El check-in avisa al médico (badge "En espera" pulsando en su agenda). El paciente espera con el chat disponible.', ruta: '/medical/teleconsulta', tituloTab: 'Teleconsulta' },
  { n: 6, titulo: 'El médico ADMITE y atiende', detalle: 'Atender → Admitir → Entrar a la sala de video. La pantalla del paciente pasa sola de la espera al video.', ruta: '/medical/teleconsulta', tituloTab: 'Teleconsulta' },
  { n: 7, titulo: 'Durante la llamada', detalle: 'Silenciar, cámara, compartir pantalla (para explicar resultados), reportar EVENTO ADVERSO, chat paralelo, y Nota médica + Receta sin salir de la sala.', ruta: '/medical/teleconsulta', tituloTab: 'Teleconsulta' },
  { n: 8, titulo: 'Finalizar con resultado', detalle: 'Efectiva, parcial o fallida (KPI obligatorio de la norma) + calidad de conexión. La duración se calcula sola.', ruta: '/medical/teleconsulta', tituloTab: 'Teleconsulta' },
  { n: 9, titulo: 'Encuestas e indicadores', detalle: 'Al salir, el paciente califica 1-5; el médico también. Todo alimenta la pestaña Indicadores (numeral 7.3.4).', ruta: '/medical/teleconsulta', tituloTab: 'Teleconsulta' },
];

const COMO: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }[] = [
  {
    q: '¿Cómo entra el paciente a su teleconsulta? (paso a paso)',
    pasos: [
      'Entra al portal con su usuario y clave (ej.: la paciente demo "etorres" / "Demo123!").',
      'En "Mis teleconsultas" ve la cita de hoy con su médico y el estado del pago.',
      'Si está "Pago pendiente": botón Pagar → De Una (QR) o Transferencia.',
      'Botón Consentimiento → lee y acepta (una sola vez por teleconsulta).',
      '"Entrar a la consulta" → prueba de cámara/micrófono → Entrar → queda EN SALA DE ESPERA ("el médico fue avisado de tu llegada").',
      'Cuando el médico lo admite, el video arranca solo — no tiene que tocar nada.',
      'Al colgar, califica la atención con estrellas y un comentario opcional.',
    ],
    enlaces: [{ to: '/medical/pacientes', t: 'Pacientes (acceso al portal)' }],
  },
  {
    q: '¿Cómo atiendo una teleconsulta como médico?',
    pasos: [
      'Teleconsulta → agenda del día: cada fila muestra consentimiento, pago y estado.',
      'Cuando el paciente hace check-in verás el badge ámbar "EN ESPERA" pulsando — ábrela con Atender.',
      'Botón ADMITIR (la sala de espera muestra desde cuándo espera) y luego "Entrar a la sala de video".',
      'Pasa tu propia prueba de cámara/mic y entra: la sesión se inicia sola al conectar.',
      'Desde la misma sala del modal: Nota médica (evolución) y Receta sin cortar la llamada.',
      'Al terminar: Salir → elige el RESULTADO (efectiva/parcial/fallida) → Finalizar. La cita queda "atendida".',
    ],
    enlaces: [{ to: '/medical/teleconsulta', t: 'Teleconsulta' }],
  },
  {
    q: '¿Cómo se cobra la teleconsulta? (3 vías)',
    pasos: [
      'La tarifa nace del PRECIO DE CONSULTA del médico (Médicos → editar). Ejemplo: Dra. García $50 → la cita telemática exige $50 antes del check-in.',
      '1) DE UNA QR: el paciente genera el QR en su portal y paga con la app del Pichincha; el sistema confirma solo. (En modo "simulado" el pago llega a los ~10 s — útil para probar.)',
      '2) TRANSFERENCIA: el paciente sube el comprobante → queda "por validar" → en la sala del médico aparece "Ver comprobante" + Aprobar/Rechazar.',
      '3) MANUAL: el staff marca Pagada (ventanilla), Convenio o Exonerada desde la misma sala.',
      'Sin pago resuelto el paciente NO puede hacer check-in (el sistema se lo dice con claridad).',
    ],
    enlaces: [{ to: '/medical/teleconsulta', t: 'Teleconsulta' }, { to: '/pos/deuna', t: 'De Una (QR)' }],
  },
  {
    q: '¿Y la factura del pago?',
    pasos: [
      'Al confirmarse el pago (cualquier vía) se emite la FACTURA SRI automáticamente a nombre del paciente.',
      'Concepto: "Teleconsulta médica" (servicio, IVA 0%), centro de costo analítico "Telemedicina".',
      'La sala del médico muestra "· facturada" junto al pago; la factura vive en el módulo Financiero como cualquier otra.',
      'Si la emisión al SRI falla en el momento, la factura queda creada y la emisión se reintenta (evento en la auditoría).',
    ],
    enlaces: [{ to: '/medical/teleconsulta', t: 'Teleconsulta' }],
  },
  {
    q: '¿Qué hago si el video falla o la conexión es mala?',
    pasos: [
      'La llamada se RECONECTA sola ante cortes breves (verás "reconectando…"); cada reconexión queda en los indicadores.',
      'Red lenta: cualquiera de los dos puede apagar su cámara ("solo audio") — clínicamente suficiente en la mayoría de controles.',
      'Si el paciente no logra entrar: revisa que pagó, aceptó el consentimiento y fue ADMITIDO; "Renovar acceso" extiende la ventana si la cita se corrió.',
      'Si se terminó por teléfono: finaliza con resultado PARCIAL — así el indicador refleja la realidad.',
      'El paciente debe usar la URL con candado (https) — sin eso el navegador bloquea su cámara y micrófono.',
    ],
    enlaces: [{ to: '/medical/teleconsulta', t: 'Teleconsulta' }],
  },
  {
    q: '¿Cómo reporto un evento adverso durante la llamada?',
    pasos: [
      'Botón "Evento adverso" en la barra de la videollamada (lo exige la norma: disponible EN la plataforma).',
      'Describe lo ocurrido (ej.: "paciente refirió mareo intenso, se indicó acudir a emergencias") y Reportar.',
      'Queda registrado con fecha, autor y teleconsulta para la dirección médica, y suma al indicador de eventos adversos.',
    ],
    enlaces: [{ to: '/medical/teleconsulta', t: 'Teleconsulta' }],
  },
  {
    q: '¿Qué indicadores exige la norma y dónde los veo?',
    pasos: [
      'Teleconsulta → pestaña INDICADORES (con rango de fechas).',
      'Efectivas/parciales/fallidas, no-show (sin check-in), duración y espera promedio, reconexiones, calidad reportada, eventos adversos, satisfacción de PACIENTES y MÉDICOS, pagos y desglose por médico.',
      'Son los mínimos del numeral 7.3.4 del A.M. 00044-2025 (Norma Técnica de Telesalud) — revisarlos mensualmente y levantar planes de mejora.',
    ],
    enlaces: [{ to: '/medical/teleconsulta', t: 'Teleconsulta' }],
  },
  {
    q: '¿Se graban las teleconsultas?',
    pasos: [
      'NO, por decisión de diseño: la norma no lo exige y grabar crea un segundo repositorio de datos sensibles (LOPDP).',
      'Lo que sí queda SIEMPRE: la nota clínica en la HCE, la receta, y la trazabilidad técnica completa (quién entró, cuándo, duración, calidad).',
      'El consentimiento trae la casilla de grabación por si a futuro se habilita un caso puntual — hoy no tiene efecto.',
    ],
    enlaces: [],
  },
  {
    q: '¿Qué necesita cada lado técnicamente?',
    pasos: [
      'Paciente: navegador moderno (Chrome/Safari/Edge) en celular o PC, cámara/micrófono y la URL https del sistema. Sin instalar nada.',
      'Médico: lo mismo, idealmente con auriculares.',
      'Ancho de banda: ~1 Mbps por lado para video estándar; con menos, usar "solo audio".',
      'El video viaja CIFRADO (DTLS-SRTP) hasta nuestro propio servidor en Ecuador — no pasa por proveedores externos (cumplimiento LOPDP).',
    ],
    enlaces: [],
  },
];

const GLOSARIO: { t: string; d: string }[] = [
  { t: 'Sala propia (LiveKit)', d: 'El servidor de video del sistema: la llamada corre en nuestra infraestructura, no en Zoom/Meet.' },
  { t: 'Check-in', d: 'El paciente "llega" a la sala de espera virtual; requiere consentimiento aceptado y pago resuelto.' },
  { t: 'Admisión', d: 'Acción del médico que deja entrar al paciente al video — nadie entra sin ser admitido.' },
  { t: 'Prepago', d: 'La cita telemática se paga ANTES de entrar (patrón estándar en telemedicina: reduce el no-show).' },
  { t: 'Por validar', d: 'Estado del pago por transferencia mientras el staff revisa el comprobante.' },
  { t: 'Resultado', d: 'Cierre obligatorio de cada teleconsulta: efectiva, parcial o fallida (KPI de la norma).' },
  { t: 'No-show', d: 'Cita telemática pasada en la que el paciente nunca hizo check-in.' },
  { t: 'Evento adverso', d: 'Incidente clínico durante la teleconsulta; se reporta desde la propia sala (numeral 7.3.3).' },
  { t: 'Solo audio', d: 'Modo para redes lentas: se apaga el video y la consulta continúa por voz.' },
  { t: 'A.M. 00044-2025', d: 'Norma Técnica de Telesalud del MSP (R.O. 153, oct-2025) — obligatoria para prestadores privados; define consentimiento, trazabilidad y KPIs.' },
  { t: 'Ventana de acceso', d: 'Período en que el token de la sala es válido; "Renovar acceso" la extiende si la consulta se retrasa.' },
];

function Acordeon({ q, children }: { q: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-md">
      <button className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-left" onClick={() => setOpen((o) => !o)}>
        {q} {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>
      {open && <div className="px-3 pb-3 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

export function AyudaTelemedicina() {
  const ir = useIr();
  const { data, isFetching } = useQuery<AyudaData>({
    queryKey: ['med-ayuda-tele-estado'], queryFn: () => medRpc('med_ayuda_tele_estado'),
    staleTime: 0, refetchOnMount: 'always',
  });

  const Ir = ({ to, t }: { to: string; t: string }) => (
    <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => ir(to, t)}>
      <Link2 className="w-3.5 h-3.5" /> {t}
    </button>
  );

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <Video className="w-5 h-5" /> Ayuda — Telemedicina
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </h1>
      <p className="text-sm text-muted-foreground -mt-2">
        Teleconsulta con video en NUESTRO servidor: sala de espera con admisión, prepago con factura automática e
        indicadores de la Norma Técnica de Telesalud. La guía del resto del módulo está en <Ir to="/medical/ayuda" t="Ayuda — Centro Médico" />
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Listo para operar</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {CHECKS.map((c) => {
            const ok = data?.checklist?.[c.key] ?? false;
            return (
              <div key={c.key} className="flex items-start gap-2 text-sm">
                {ok
                  ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
                  : <XCircle className={`w-4 h-4 mt-0.5 shrink-0 ${c.advisory ? 'text-amber-500' : 'text-red-500'}`} />}
                <div>
                  <span className={ok ? '' : 'font-medium'}>{c.label}</span>
                  {c.advisory && !ok && <span className="ml-1 rounded bg-amber-100 px-1.5 text-[10px] uppercase text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">opcional</span>}
                  <span className="text-muted-foreground"> — {c.ayuda}</span>{' '}
                  {!ok && <Ir to={c.ruta} t={c.titulo} />}
                </div>
              </div>
            );
          })}
          {data && (
            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
              Ahora: {data.estado.tele_hoy} teleconsulta(s) hoy · {data.estado.en_espera} paciente(s) en sala de espera · {data.estado.efectivas_mes} efectiva(s) este mes
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">El flujo de la teleconsulta</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FLUJO.map((f) => (
            <div key={f.n} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{f.n}</div>
              <div>
                <span className="font-medium">{f.titulo}.</span>{' '}
                <span className="text-muted-foreground">{f.detalle}</span>{' '}
                <Ir to={f.ruta} t={f.tituloTab} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">¿Cómo hago…?</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {COMO.map((c) => (
            <Acordeon key={c.q} q={c.q}>
              <ol className="list-decimal pl-5 space-y-1">{c.pasos.map((p, i) => <li key={i}>{p}</li>)}</ol>
              {c.enlaces.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-3">{c.enlaces.map((e) => <Ir key={e.to} to={e.to} t={e.t} />)}</div>
              )}
            </Acordeon>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Glosario</CardTitle></CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2">
          {GLOSARIO.map((g) => (
            <div key={g.t} className="text-sm"><span className="font-medium">{g.t}:</span> <span className="text-muted-foreground">{g.d}</span></div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
